/**
 * «Vincular campaña» desde la ficha de una unidad / lote / parcela (funciones puras).
 * La lista sale de `GET /getmetacampaigns` (`readMetaCampaigns`) y el estado de cada campaña
 * respecto del registro se calcula con los `linkedRecords` de sus anuncios.
 * Ver docs/maps/meta-ads.md § Vincular campaña desde la ficha.
 */
import { describeMetaRecord, metaRecordKey } from './metaCampaigns';
import { normalizeSearchText, parseSearchTerms } from '../Map/utils/recordSearch';
import { formatMetaMoney } from './metaAdsMoney';
import { formatMetaContacts } from './metaContacts';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * Campaña sin anuncios para vincular (APIBOT: se filtran antes; el 409
 * `META_CAMPAIGN_NO_ADS` queda de respaldo). `ads_total_count` manda si viene.
 */
export function metaCampaignHasNoAds(campaign) {
  if (!campaign) return true;
  if (campaign.adsTotalCount === 0) return true;
  return (campaign.ads || []).length === 0 && !(Number(campaign.adsCount) > 0);
}

/**
 * Estado de la campaña para ESTE registro: `linked` (todos sus anuncios ya lo promocionan),
 * `partial` (algunos) o `unlinked`. `others` = las otras unidades vinculadas (sin repetir).
 *
 * @returns {{ status: 'linked'|'partial'|'unlinked', linkedAds: number, totalAds: number, others: object[] }}
 */
export function metaCampaignUnitState(campaign, record) {
  const key = metaRecordKey(record);
  const ads = campaign?.ads || [];
  let linkedAds = 0;
  const others = new Map();
  ads.forEach((ad) => {
    let here = false;
    (ad.linkedRecords || []).forEach((rec) => {
      const k = metaRecordKey(rec);
      if (k === key) here = true;
      else if (!others.has(k)) others.set(k, rec);
    });
    if (here) linkedAds += 1;
  });
  const totalAds = ads.length;
  let status = 'unlinked';
  if (totalAds > 0 && linkedAds === totalAds) status = 'linked';
  else if (linkedAds > 0) status = 'partial';
  return { status, linkedAds, totalAds, others: [...others.values()] };
}

/** «También en Dúplex 2» / «También en Dúplex 2 y 2 más» ('' sin otras unidades). */
export function describeMetaOtherUnits(others) {
  const list = Array.isArray(others) ? others : [];
  if (list.length === 0) return '';
  const first = list[0]?.label || describeMetaRecord(list[0]);
  return list.length === 1 ? `También en ${first}` : `También en ${first} y ${list.length - 1} más`;
}

/**
 * Filas del selector: sin campañas vacías, filtradas por nombre (misma normalización que
 * Historial / Campañas) y con las ya vinculadas al final (orden de la API dentro de cada grupo).
 */
export function metaUnitLinkRows(campaigns, record, search = '') {
  const terms = parseSearchTerms(search);
  const rows = (Array.isArray(campaigns) ? campaigns : [])
    .filter((c) => !metaCampaignHasNoAds(c))
    .filter((c) => !terms.length || terms.every((t) => normalizeSearchText(c.name).includes(t)))
    .map((campaign) => ({ campaign, ...metaCampaignUnitState(campaign, record) }));
  return [...rows.filter((r) => r.status !== 'linked'), ...rows.filter((r) => r.status === 'linked')];
}

/** «Gasto ARS 114.149 · 31 contactos» (nombres completos; '' sin datos). */
export function metaUnitLinkMetrics(campaign) {
  const metrics = campaign?.metrics;
  if (!metrics) return '';
  const parts = [];
  if (metrics.spend != null && campaign.currency) parts.push(`Gasto ${formatMetaMoney(metrics.spend, campaign.currency)}`);
  const contacts = formatMetaContacts(metrics);
  if (contacts) parts.push(contacts);
  return parts.join(' · ');
}

/** Segunda línea: objetivo · «N anuncios» · estado si no está activa. */
export function metaUnitLinkDetail(campaign, state) {
  const ads = state?.status === 'partial'
    ? `${state.linkedAds} de ${plural(state.totalAds, 'anuncio', 'anuncios')} ya vinculados`
    : plural(Number(campaign?.adsCount) || (campaign?.ads || []).length, 'anuncio', 'anuncios');
  const inactive = campaign?.isActive === false && campaign?.statusLabel ? campaign.statusLabel : null;
  return [campaign?.objectiveLabel, ads, inactive].filter(Boolean).join(' · ');
}

/** Primer anuncio con miniatura (la campaña no trae una propia). */
export function metaCampaignThumb(campaign) {
  const ad = (campaign?.ads || []).find((a) => a.thumbnailUrl) || (campaign?.ads || [])[0] || null;
  return { url: ad?.thumbnailUrl || null, isVideo: Boolean(ad?.isVideo) };
}
